import React, { useContext } from 'react'

import { useQuery } from '@tanstack/react-query'

import { Icon, ArrowRight, Button, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import axios from 'axios'

import { useLocation } from 'Pods/Shared/ContextBasedRouter'
import { parseGeneratingRunId } from 'Pods/ThreeDStudio/Hooks/generatingRunUrl'
import { parseRunIdFromPathname } from 'Pods/ThreeDStudio/Hooks/parseRunIdFromPathname'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { STUDIO_EVENT, trackStudioEvent } from 'Pods/ThreeDStudio/Services/studioTracker'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

const RECOMMENDATIONS_PATH = '/api/internal/generator/recommendations'
// The design shows a single row of suggestions, which is one full grid row wide.
const SUGGESTION_COUNT = 4

function RelatedProducts() {
  const t = useTranslation()
  const { extractingCoreStyles } = useContext(ThreeDStudioPageContext)
  // The URL names the run on screen, and unlike the store it survives a reload - a restored
  // run has no openedModel payload to read from. `?run=` covers an in-flight run and the
  // path covers a shared finished run being loaded; those are the only two states this
  // banner renders in, so there is nothing to fall back to. Reading the store's latest run
  // instead would recommend for whichever run started last, no matter which one the user
  // is actually watching.
  const location = useLocation()
  const generatingRunId = parseGeneratingRunId(location?.search ?? '')
  const openedRunId = parseRunIdFromPathname(location?.pathname ?? '')
  const runId = generatingRunId ?? openedRunId
  const { data, isLoading: queryLoading } = useQuery({
    queryKey: ['generatorRecommendations', runId],
    queryFn: async () => {
      const { data: response } = await axios.get(RECOMMENDATIONS_PATH, {
        params: { per_page: SUGGESTION_COUNT, run_id: runId },
      })

      const items = response.items ?? response.data?.items ?? []

      return {
        searchKeywords: response.title ?? response.data?.title ?? '',
        items: items.map((item) => {
          const attrs = item.attributes || {}
          const primaryImage = attrs.primaryImage || {}
          return {
            key: item.id,
            href: attrs.url || '#',
            imagePath: primaryImage.gridUrl || primaryImage.largeUrl || '',
            imageAlt: attrs.imageAlt || attrs.title || 'Related product',
          }
        }),
      }
    },
    enabled: Boolean(runId),
  })

  const relatedProducts = data?.items ?? []
  const searchKeywords = data?.searchKeywords ?? ''
  const viewMoreHref = searchKeywords
    ? `/search?keywords=${encodeURIComponent(searchKeywords)}`
    : '/3d-models?page=1'

  const isLoading = extractingCoreStyles || queryLoading
  const slots = isLoading ? [] : relatedProducts
  const isEmpty = !isLoading && slots.length === 0

  const renderRelatedProducts = () => (
    Array.from({ length: SUGGESTION_COUNT }, (_, index) => {
      const product = slots[index]

      if (!product) {
        return <div className="cgt-model-card__image-container related-products__loading-card shimmer" key={index} />
      }

      return (
        <a
          key={product.key}
          className="related-products__list-item"
          href={product.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackStudioEvent(STUDIO_EVENT.MARKETPLACE_SUGGESTION_CLICK, {
            item_id: product.key,
            index: index + 1,
            run_id: runId,
          })}
        >
          <img
            className="related-products__list-item-image"
            src={product.imagePath}
            alt={product.imageAlt}
            height={105}
            width={140}
          />
        </a>
      )
    })
  )

  return (
    <div
      className={classnames('related-products', isEmpty && 'related-products--empty')}
      aria-hidden={isEmpty ? 'true' : undefined}
    >
      <div className="related-products__header">
        <span className="related-products__title cgt-heading-50">{t('viewer.banner.relatedProducts.title')}</span>
        <Button
          disabled={isLoading}
          trailIcon={<Icon svgComponent={ArrowRight} size="small" />}
          type="ghost-primary"
          variant="with-icon"
          href={isLoading ? '' : viewMoreHref}
          customClass="related-products__view-more"
          linkAttributes={{ target: '_blank', rel: 'noopener noreferrer' }}
        >
          {t('viewer.banner.relatedProducts.viewMore')}
        </Button>
      </div>
      <div className="related-products__list">{renderRelatedProducts()}</div>
    </div>
  )
}

export default RelatedProducts
